import {
  Divider, ErrorMessages, IconButton, IconButtonType, Page, Spinner
} from "@whitespace/whitespace-ui";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Breadcrumb,
  Button, Card, Col, Container, Form, FormGroup, Row
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { useCallback, useEffect, useState } from "react";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";

import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import IAddAuthorisedMaintenanceRequestModel from "interfaces/Request/IAddAuthorisedMaintenanceRequestModel";
import authorisedMaintenanceSchema from "modules/schemas/authorisedMaintenanceSchema";

const AddAuthorisedMaintenance = () => {
  const navigate = useNavigate();
  const { hwrcSiteId, diaryDate } = useParams();
  const returnUrl = `${RoutePathsEnum.SiteDiaryLandingPage}${hwrcSiteId && diaryDate ? `/${hwrcSiteId}/${diaryDate}` : ""}`;
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [addButtonDisabled, setAddButtonDisabled] = useState<boolean>(true);

  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues
  } = useForm<IAddAuthorisedMaintenanceRequestModel>({
    resolver: yupResolver(authorisedMaintenanceSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const {
    isSending,
    errors: apiErrors,
    sendRequest,
    statusCode,
    resetState
  } = useAmplifyREST();

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const handleCreateAuthorisedMaintenanceEntryClick = (request : IAddAuthorisedMaintenanceRequestModel) => {
    resetState();
    sendRequest(`hwrcsitediary/hwrcsite/${hwrcSiteId}/diary/${diaryDate}/authorisedmaintenance`, "POST", request);
  };

  const canFormBeSubmitted = () => {
    const mandatoryFieldEntered = (getValues("atwPtwNumber").length > 0 && getValues("undertakenBy").length > 0);
    setAddButtonDisabled(!mandatoryFieldEntered);
  };

  useEffect(() => {
    if (statusCode === 200) {
      navigate(returnUrl);
    }
  }, [statusCode]);

  useEffect(() => {
    if (statusCode === 401) {
      navigate(RoutePathsEnum.Unauthorised);
    }
  }, [statusCode]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <h2 data-testid="card-header">
            Add Maintenance Entry
            <Spinner
              animation="grow"
              visible={isSending}
              spinnerText="Loading"
              variant="info"
            />
          </h2>
        </Card.Header>
        <Card.Body className="no-padding">
          <Form
            autoComplete="off"
            onSubmit={handleSubmit(handleCreateAuthorisedMaintenanceEntryClick)}
          >
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-diary"
                    linkAs={Link}
                    linkProps={{ to: returnUrl }}
                    onClick={() => navigate(returnUrl)}
                  >
                    Diary
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active data-testid="breadcrumb-add-authorised-maintenance">
                    Add Maintenance
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>

              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={addButtonDisabled || isSending}
                  aria-disabled={addButtonDisabled || isSending}
                  type="submit"
                  spin={isSending}
                  data-testid="create"
                  aria-label="create authorised maintenance entry"
                >
                  Create
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(returnUrl)}
                  data-testid="cancel"
                >
                  Cancel
                </Button>
              </Col>
            </Row>

            <Divider />
            <section className="authorised-visitor-temporary-worker pt-2">
              <Container fluid>
                <Row>
                  <div className="control-pane">
                    <div className="control-section" />

                    <Row className="mt-3">
                      <FormGroup as={Row} controlId="undertakenBy">
                        <Form.Label id="undertakenByLabel" className="horiz-form-label">
                          Undertaken By:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-labelledby="undertakenByLabel"
                            aria-invalid={ariaInvalidCheck(
                              "undertakenBy",
                              errors,
                              null
                            )}
                            maxLength={50}
                            minLength={1}
                            onKeyUp={() => canFormBeSubmitted()}
                            {...register("undertakenBy")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="undertakenBy"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>
                    <Row className="mt-3">
                      <FormGroup as={Row} controlId="atwPtwNumber">
                        <Form.Label id="atwPtwNumberLabel" className="horiz-form-label">
                          ATW/PTW Number:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-labelledby="atwPtwNumberLabel"
                            aria-invalid={ariaInvalidCheck(
                              "atwPtwNumber",
                              errors,
                              null
                            )}
                            maxLength={50}
                            minLength={1}
                            onKeyUp={() => canFormBeSubmitted()}
                            {...register("atwPtwNumber")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="atwPtwNumber"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>
                    <Row className="mt-3 mb-3">
                      <FormGroup as={Row} controlId="details">
                        <Form.Label id="detailsLabel" className="horiz-form-label">
                          Details:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="false"
                            aria-labelledby="detailsLabel"
                            aria-invalid={ariaInvalidCheck(
                              "details",
                              errors,
                              null
                            )}
                            maxLength={50}
                            minLength={1}
                            onKeyUp={() => canFormBeSubmitted()}
                            {...register("details")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="details"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>

                  </div>
                </Row>
              </Container>

            </section>

          </Form>
        </Card.Body>
      </Card>
    </Page>
  );
};

export default AddAuthorisedMaintenance;
